import { MouseEvent, ReactElement, useState } from 'react'
import { elementTypeToLabelSingular } from '@domainConstants'
import { v4 } from 'uuid'
import * as yup from 'yup'
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined'
import { IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { PopoverPanel } from '@ui2/structure'
import { Form2, SubmitButton, TextField } from '@ui/forms'
import { NavbarPortalAiActions } from '@ui/navigation'
import { useSnackbar } from '@ui/snackbar'
import { useAppStore } from '@stores'
import { useElementLabel, useElementTypes } from '@editorHooks'
import useHasAiPermission from 'src/hooks/useHasAiPermission'
import usePersistentParams from 'src/hooks/usePersistentParams'
import useSelectedElementGuids from './useSelectedElementGuids'

interface InstructionFormValues {
  text: string
}

const validationSchema = yup.object({
  text: yup.string().trim().required('Bitte eine Instruktion eingeben'),
})

/**
 * Note button in the header bar (access:ai users only): write down an
 * instruction for the AI agent about the currently selected scene elements.
 * The instructions are collected per project in the KI-Zugriff widget's
 * "Instruktionen" tab, ready to be copied for the agent.
 */
const AIInstructionNoteButton = (): ReactElement | null => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const { enqueueSnackbar } = useSnackbar()
  const { hasAiPermission } = useHasAiPermission()
  const { projectId }: { projectId?: string } = usePersistentParams()

  const selectedGuids = useSelectedElementGuids()
  const addAiInstruction = useAppStore(state => state.addAiInstruction)

  const getLabel = useElementLabel()
  const elementTypes = useElementTypes()

  if (!hasAiPermission || !projectId) return null

  const selectedLabels = selectedGuids.map(guid => getLabel(guid)).join(', ')

  const handleToggle = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(prev => (prev ? null : event.currentTarget))
  }

  const handleSubmit = (values: InstructionFormValues) => {
    const elements = selectedGuids.map(guid => {
      const elementType = elementTypes[guid]
      return {
        guid,
        label: getLabel(guid),
        elementTypeLabel: elementType ? elementTypeToLabelSingular[elementType] : null,
      }
    })

    addAiInstruction(projectId, {
      id: v4(),
      text: values.text.trim(),
      elements,
      createdAt: new Date().toISOString(),
    })
    enqueueSnackbar('Instruktion hinzugefügt', { variant: 'success' })
    setAnchorEl(null)
  }

  return (
    <NavbarPortalAiActions>
      <Tooltip title="Instruktion für die KI notieren">
        <IconButton
          size="small"
          sx={{ color: 'rgba(0, 0, 0, 0.54)', width: 32, height: 32 }}
          onClick={handleToggle}
          data-cy="ai-instruction-note-button"
        >
          <NoteAddOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {anchorEl && (
        <PopoverPanel
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          title="Instruktion für die KI"
          dataCy="ai-instruction-note-panel"
        >
          <Stack spacing={1.5} sx={{ width: 340 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              data-cy="ai-instruction-note-elements"
            >
              {selectedLabels
                ? `Betroffene Elemente: ${selectedLabels}`
                : 'Keine Elemente ausgewählt'}
            </Typography>
            <Form2<InstructionFormValues>
              onSubmit={handleSubmit}
              defaultValues={{ text: '' }}
              validationSchema={validationSchema}
            >
              <Stack spacing={1}>
                <TextField
                  name="text"
                  placeholder="Instruktion eingeben"
                  size="small"
                  autoFocus
                  data-cy="ai-instruction-note-input"
                />
                <SubmitButton
                  loading={false}
                  text="Hinzufügen"
                  data-cy="ai-instruction-note-submit"
                />
              </Stack>
            </Form2>
          </Stack>
        </PopoverPanel>
      )}
    </NavbarPortalAiActions>
  )
}

export default AIInstructionNoteButton
